/**
 * 批量下载弹窗组件
 * 提供分批获取下载链接、分批下载图片到本地的功能
 * 显示下载进度条，只有在下载完成后才能关闭
 */

import type { ReactElement } from 'react'
import { useState, useCallback, useRef } from 'react'
import { Download, CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { getBatchTaskResults } from '@/services/taskService'
import { TASK_RESULT_STATUS } from '@/types/task'
import type { BatchTaskResultItem } from '@/types/task'

/**
 * 下载状态枚举
 */
const DOWNLOAD_STATUS = {
  IDLE: 'idle',
  FETCHING: 'fetching',
  DOWNLOADING: 'downloading',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const

type DownloadStatus = (typeof DOWNLOAD_STATUS)[keyof typeof DOWNLOAD_STATUS]

/**
 * 下载模式枚举
 */
export const DOWNLOAD_MODE = {
  IMAGES: 'images',
  URLS: 'urls',
} as const

export type DownloadMode = (typeof DOWNLOAD_MODE)[keyof typeof DOWNLOAD_MODE]

/**
 * 组件属性
 */
interface BatchDownloadDialogProps {
  /** 是否打开弹窗 */
  open: boolean
  /** 弹窗状态变化回调 */
  onOpenChange: (open: boolean) => void
  /** 批量任务 ID */
  batchId: string
  /** 任务备注 */
  remark?: string
  /** 预期下载数量（可选，用于显示） */
  expectedCount?: number
  /** 下载模式：images - 下载图片，urls - 导出地址 */
  mode?: DownloadMode
  /** 指定要下载的任务 ID 列表（可选，如果提供则只下载这些任务） */
  selectedTaskIds?: string[]
}

/**
 * 下载进度信息
 */
interface DownloadProgress {
  /** 总数 */
  total: number
  /** 已下载数 */
  downloaded: number
  /** 失败数 */
  failed: number
  /** 当前状态描述 */
  message: string
}

/**
 * 每批获取的任务数量
 */
const FETCH_BATCH_SIZE = 50

/**
 * 隐藏下载 iframe 的 DOM ID
 */
const DOWNLOAD_IFRAME_ID = 'batch-download-iframe' as const

/**
 * 隐藏下载 iframe 的 name（用于 a 标签 target）
 */
const DOWNLOAD_IFRAME_NAME = 'batch-download-iframe' as const

/**
 * 确保页面中存在隐藏的下载 iframe
 * 用于在不跳转页面的情况下触发浏览器下载
 * @returns iframe 元素
 */
function ensureDownloadIframe(): HTMLIFrameElement {
  const existing = document.getElementById(DOWNLOAD_IFRAME_ID)
  if (existing instanceof HTMLIFrameElement) {
    return existing
  }

  const iframe = document.createElement('iframe')
  iframe.id = DOWNLOAD_IFRAME_ID
  iframe.name = DOWNLOAD_IFRAME_NAME
  iframe.style.display = 'none'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.setAttribute('aria-hidden', 'true')
  document.body.appendChild(iframe)
  return iframe
}

/**
 * 触发浏览器下载（通过隐藏 iframe，避免页面跳转）
 * @param url 下载链接
 * @param filename 文件名（可选）
 */
function triggerBrowserDownload(url: string, filename?: string): void {
  void ensureDownloadIframe()

  const link = document.createElement('a')
  link.href = url
  if (filename !== undefined && filename !== '') {
    link.download = filename
  }
  link.target = DOWNLOAD_IFRAME_NAME
  link.rel = 'noopener noreferrer'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * 从 URL 中提取文件名
 * @param url 文件 URL
 * @param index 文件索引
 * @returns 文件名
 */
function extractFilename(url: string, index: number): string {
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    const segments = pathname.split('/')
    const lastSegment = segments[segments.length - 1] ?? ''

    // 如果有文件名，使用原文件名
    if (lastSegment !== '' && lastSegment.includes('.')) {
      return decodeURIComponent(lastSegment)
    }

    // 否则使用默认名称
    return `image_${String(index + 1).padStart(4, '0')}.png`
  } catch {
    return `image_${String(index + 1).padStart(4, '0')}.png`
  }
}

/**
 * 等待指定毫秒
 * @param ms 等待时间（毫秒）
 */
async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * 导出 URL 到 CSV 文件
 * @param urls URL 列表
 * @param filename 文件名
 */
function exportUrlsToCsv(urls: string[], filename: string): void {
  // 创建 CSV 内容
  const csvContent = urls.join('\n')

  // 创建 Blob
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const blobUrl = URL.createObjectURL(blob)

  // 创建下载链接
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // 释放 blob URL
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl)
  }, 100)
}

/**
 * 批量下载弹窗组件
 * @param props 组件属性
 * @returns 弹窗 ReactElement
 */
export function BatchDownloadDialog({
  open,
  onOpenChange,
  batchId,
  remark,
  expectedCount,
  mode = DOWNLOAD_MODE.IMAGES,
  selectedTaskIds,
}: BatchDownloadDialogProps): ReactElement {
  // 下载状态
  const [status, setStatus] = useState<DownloadStatus>(DOWNLOAD_STATUS.IDLE)
  const [progress, setProgress] = useState<DownloadProgress>({
    total: 0,
    downloaded: 0,
    failed: 0,
    message: '准备下载...',
  })
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // 用于中断下载的标志
  const abortRef = useRef(false)
  // 用于追踪是否已经开始下载
  const hasStartedRef = useRef(false)

  /**
   * 重置状态
   */
  const resetState = useCallback((): void => {
    setStatus(DOWNLOAD_STATUS.IDLE)
    setProgress({
      total: 0,
      downloaded: 0,
      failed: 0,
      message: '准备下载...',
    })
    setErrorMessage(null)
    abortRef.current = false
    hasStartedRef.current = false
  }, [])

  /**
   * 分批获取所有成功的任务结果
   */
  const fetchAllSuccessfulResults = useCallback(async (): Promise<BatchTaskResultItem[]> => {
    const allResults: BatchTaskResultItem[] = []
    let page = 1
    let hasMore = true

    while (hasMore && !abortRef.current) {
      setProgress((prev) => ({
        ...prev,
        message: `正在获取任务列表 (第 ${String(page)} 页)...`,
      }))

      const response = await getBatchTaskResults(batchId, page, FETCH_BATCH_SIZE)

      // 过滤出成功的任务
      let successResults = response.results.items.filter(
        (item) => item.status === TASK_RESULT_STATUS.COMPLETED && item.output_url !== null
      )

      // 如果指定了 selectedTaskIds，则进一步过滤
      if (selectedTaskIds !== undefined && selectedTaskIds.length > 0) {
        successResults = successResults.filter((item) =>
          selectedTaskIds.includes(item.task_id)
        )
      }

      allResults.push(...successResults)

      // 如果指定了 selectedTaskIds 并且已经找到所有选中的任务，提前终止
      if (selectedTaskIds !== undefined && allResults.length >= selectedTaskIds.length) {
        hasMore = false
      } else {
        // 检查是否还有更多数据
        const totalPages = Math.ceil(response.results.total / FETCH_BATCH_SIZE)
        hasMore = page < totalPages
      }
      page += 1
    }

    return allResults
  }, [batchId, selectedTaskIds])

  /**
   * 开始下载流程
   */
  const startDownload = useCallback(async (): Promise<void> => {
    if (status === DOWNLOAD_STATUS.FETCHING || status === DOWNLOAD_STATUS.DOWNLOADING) {
      return
    }

    abortRef.current = false
    setStatus(DOWNLOAD_STATUS.FETCHING)
    setErrorMessage(null)

    try {
      if (mode === DOWNLOAD_MODE.URLS) {
        // 导出 URL 模式（仅导出已完成的图片地址）
        const allResults = await fetchAllSuccessfulResults()

        // 检查是否在获取过程中被中断（用户关闭弹窗）
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- abortRef 可能在 async 过程中被修改
        if (abortRef.current) {
          return
        }

        if (allResults.length === 0) {
          setErrorMessage('没有可导出的图片地址')
          setStatus(DOWNLOAD_STATUS.ERROR)
          return
        }

        const urls = allResults.map((item) => item.output_url ?? '').filter((url) => url !== '')
        const filename = remark !== undefined && remark !== ''
          ? `${remark}_生成图片地址.csv`
          : '生成图片地址.csv'

        exportUrlsToCsv(urls, filename)

        // 直接完成
        setStatus(DOWNLOAD_STATUS.COMPLETED)
        setProgress({
          total: urls.length,
          downloaded: urls.length,
          failed: 0,
          message: `已导出 ${String(urls.length)} 个图片地址到 CSV 文件`,
        })
      } else {
        // 下载图片模式：使用 a 标签触发浏览器下载（避免 fetch 的 CORS 限制）
        const allResults = await fetchAllSuccessfulResults()

        // 检查是否在获取过程中被中断（用户关闭弹窗）
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- abortRef 可能在 async 过程中被修改
        if (abortRef.current) {
          return
        }

        if (allResults.length === 0) {
          setErrorMessage('没有可下载的图片')
          setStatus(DOWNLOAD_STATUS.ERROR)
          return
        }

        setProgress({
          total: allResults.length,
          downloaded: 0,
          failed: 0,
          message: `正在下载 1/${String(allResults.length)}...`,
        })
        setStatus(DOWNLOAD_STATUS.DOWNLOADING)

        for (let index = 0; index < allResults.length; index += 1) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- abortRef 可能在 async 过程中被修改
          if (abortRef.current) {
            return
          }

          const result = allResults[index]
          if (result === undefined) {
            setProgress((prev) => ({
              ...prev,
              failed: prev.failed + 1,
            }))
            continue
          }

          const url = result.output_url

          if (url === null) {
            setProgress((prev) => ({
              ...prev,
              failed: prev.failed + 1,
            }))
            continue
          }

          const filename = extractFilename(url, index)
          triggerBrowserDownload(url, filename)

          setProgress((prev) => ({
            ...prev,
            downloaded: prev.downloaded + 1,
            message: `正在下载 ${String(prev.downloaded + 2)}/${String(prev.total)}...`,
          }))

          // 小延迟，减少被浏览器拦截多文件下载的概率
          await sleep(200)
        }

        // 下载行为由浏览器接管，这里仅表示“已触发下载”
        setStatus(DOWNLOAD_STATUS.COMPLETED)
        setProgress((prev) => ({
          ...prev,
          message: `已发起下载：共 ${String(prev.total)} 张（请在浏览器下载列表查看）`,
        }))
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- abortRef 可能在 async 过程中被修改
      if (!abortRef.current) {
        const message = error instanceof Error ? error.message : '下载失败，请重试'
        setErrorMessage(message)
        setStatus(DOWNLOAD_STATUS.ERROR)
      }
    }
  }, [status, fetchAllSuccessfulResults, mode, remark])

  /**
   * 处理弹窗关闭
   */
  const handleOpenChange = useCallback((newOpen: boolean): void => {
    // 只有在完成或错误状态时才能关闭
    if (!newOpen && status !== DOWNLOAD_STATUS.COMPLETED && status !== DOWNLOAD_STATUS.ERROR && status !== DOWNLOAD_STATUS.IDLE) {
      return
    }

    if (!newOpen) {
      abortRef.current = true
      resetState()
    }

    onOpenChange(newOpen)
  }, [status, onOpenChange, resetState])

  /**
   * 处理弹窗打开后的初始化
   * 在 Dialog 的 onOpenAutoFocus 中调用，避免在 useEffect 中调用 setState
   */
  const handleDialogOpen = useCallback((): void => {
    if (!hasStartedRef.current && status === DOWNLOAD_STATUS.IDLE) {
      hasStartedRef.current = true
      void startDownload()
    }
  }, [status, startDownload])

  // 计算进度百分比
  const progressPercent = progress.total > 0
    ? Math.round(((progress.downloaded + progress.failed) / progress.total) * 100)
    : 0

  // 判断是否可以关闭
  const canClose = status === DOWNLOAD_STATUS.COMPLETED || status === DOWNLOAD_STATUS.ERROR || status === DOWNLOAD_STATUS.IDLE

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={(e): void => {
          e.preventDefault()
          handleDialogOpen()
        }}
        onPointerDownOutside={(e): void => {
          if (!canClose) {
            e.preventDefault()
          }
        }}
        onEscapeKeyDown={(e): void => {
          if (!canClose) {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            {mode === DOWNLOAD_MODE.URLS ? '导出图片地址' : '批量下载'}
          </DialogTitle>
          <DialogDescription>
            {selectedTaskIds !== undefined && selectedTaskIds.length > 0 ? (
              `已选中 ${String(selectedTaskIds.length)} 张图片`
            ) : (
              remark !== undefined && remark !== '' ? remark : (mode === DOWNLOAD_MODE.URLS ? '正在导出生成的图片地址' : '正在下载生成的图片')
            )}
            {expectedCount !== undefined && expectedCount > 0 && selectedTaskIds === undefined && (
              <span className="ml-1 text-muted-foreground">
                (预计 {expectedCount} 张)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* 进度条 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{progress.message}</span>
              <span className="font-medium">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* 统计信息 */}
          {progress.total > 0 && (
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-muted-foreground">成功</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  {progress.downloaded}
                </span>
              </div>
              {progress.failed > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  <span className="text-muted-foreground">失败</span>
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {progress.failed}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-muted" />
                <span className="text-muted-foreground">总计</span>
                <span className="font-medium">{progress.total}</span>
              </div>
            </div>
          )}

          {/* 状态提示 */}
          <div className="flex items-center justify-center">
            {status === DOWNLOAD_STATUS.FETCHING && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>正在获取下载列表...</span>
              </div>
            )}
            {status === DOWNLOAD_STATUS.DOWNLOADING && (
              <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>正在下载，请勿关闭此窗口...</span>
              </div>
            )}
            {status === DOWNLOAD_STATUS.COMPLETED && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                <CheckCircle className="h-4 w-4" />
                <span>下载完成！</span>
              </div>
            )}
            {status === DOWNLOAD_STATUS.ERROR && (
              <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                <XCircle className="h-4 w-4" />
                <span>{errorMessage ?? '下载失败'}</span>
              </div>
            )}
          </div>

          {/* 提示信息 */}
          {(status === DOWNLOAD_STATUS.FETCHING || status === DOWNLOAD_STATUS.DOWNLOADING) && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  下载过程中请勿关闭此窗口。如浏览器提示“允许多文件下载”，请点击允许。
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {status === DOWNLOAD_STATUS.ERROR && (
            <Button
              variant="outline"
              onClick={(): void => {
                resetState()
                void startDownload()
              }}
            >
              重试
            </Button>
          )}
          <Button
            variant={canClose ? 'default' : 'secondary'}
            disabled={!canClose}
            onClick={(): void => {
              handleOpenChange(false)
            }}
            className={cn(!canClose && 'cursor-not-allowed opacity-50')}
          >
            {canClose ? '关闭' : '下载中...'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
